import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaygService } from '../../services/payg.service';
import { PaygSummaryModel } from '../../models/payg/payg-summary.model';
import { PaygPaymentTypeModel } from '../../models/payg/payg-payment-type.model';
import { PaygCumulativeCreditStatusModel } from '../../models/payg/payg-cumulative-credit-status.model';
import { PaygPaymentTransmissionPerformanceModel } from '../../models/payg/payg-payment-transmission-performance.model';
import { SiteService } from '../../services/site.service';
import { Observable, Subject, Subscription } from 'rxjs';
import swal from 'sweetalert2';
import { getQueryParamFromAncestorRoute } from '../../utilities/route.utilities';
import { MasonryItemModel } from '../masonry/masonry.component';
import { CumulativeCreditStatusGridItemComponent } from './grid-items/cumulative-credit-status-grid-item.component';
import { PaygPaymentsByTypeGridItemComponent } from './grid-items/payg-payments-by-type-grid-item.component';
import { PaygPaymentTransnissonPerformanceGridItemComponent } from './grid-items/payg-payment-transmission-performance-grid-item.component';

@Component({
    selector: 'app-payg-portfolio',
    templateUrl: './payg-portfolio.component.html',
    styleUrls: ['./payg-portfolio.component.scss']
})
export class PaygPortfolioComponent implements OnInit, OnDestroy {

    subscriptions: Subscription[] = [];
    masonryItems: MasonryItemModel[] = [];

    clientId: number;
    sites: any[] = [];
    selectedSiteId: number = 0;
    paygConsumersPercentage: number = 0;
    websiteUsersPercentage: number = 0;
    isDecreasing: boolean = false;
    isIncreasing: boolean = false;

    paygSummary: PaygSummaryModel = new PaygSummaryModel;
    paymentByType: PaygPaymentTypeModel[] = [];
    cumulativeCreditStatus: PaygCumulativeCreditStatusModel[] = [];
    paymentTransmissionPerformance: PaygPaymentTransmissionPerformanceModel[] = [];
    userSiteId: number = 0;

    @ViewChild('timePeriod', { static: true }) timePeriod: ElementRef;
    @ViewChild('siteId', { static: true }) siteId: ElementRef;


    constructor(
        private siteService: SiteService,
        private paygService: PaygService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngOnInit() {

            this.masonryItems = [
                {
                    component: CumulativeCreditStatusGridItemComponent
                },
                {
                    component: PaygPaymentsByTypeGridItemComponent
                },
                {
                    component: PaygPaymentTransnissonPerformanceGridItemComponent,
                }
            ]
    

        this.subscriptions.push(this.route
            .parent!
            .params
            .subscribe(params => {
                this.clientId = +params['clientId'];
                this.search();
            }));
        this.userSiteId = parseInt(getQueryParamFromAncestorRoute(this.route, 'siteId'));
        if (Number.isNaN(this.userSiteId) || this.userSiteId === undefined) { this.userSiteId = 0; }
        let timePeriod = 12;
        if (this.route.snapshot.queryParams['timePeriod']) {
            this.timePeriod.nativeElement.value =
                parseFloat(this.route.snapshot.queryParams['timePeriod']);
        } else {
            this.updateTimeperiod(timePeriod);
        }

        let siteId: number = 0;
        if (this.route.snapshot.queryParams['siteId']) {
            this.selectedSiteId = this.userSiteId;
        } else {
            this.updateSite();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    updateTimeperiod(value: any) {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        queryParams.timePeriod = +value;
        this.router.navigate(['/portal/' + this.clientId.toString() + '/portfolio'], { queryParams: queryParams, replaceUrl: true });
    }

    updateSite() {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        if (this.selectedSiteId != undefined && this.selectedSiteId != null) queryParams.siteId = +this.selectedSiteId;
        this.router.navigate(['/portal/' + this.clientId.toString() + '/portfolio'], { queryParams: queryParams, replaceUrl: true });
        this.search(this.selectedSiteId);
    }

    search(siteId?: number) {
        this.paygSummary = undefined;
        this.paymentByType = [];
        this.cumulativeCreditStatus = [];
        this.paymentTransmissionPerformance = [];
        this.websiteUsersPercentage = undefined;
        this.paygConsumersPercentage = undefined;

        this.siteService.all(this.clientId).then((result) => {
            this.sites = result;
        });

        this.paygService.getSummary(this.clientId, siteId).then((result) => {
            this.paygSummary = result;

            this.paygSummary.avgPaymentLastMonth = Math.round(this.paygSummary.avgPaymentLastMonth);
            this.paygSummary.avgPaymentThisMonth = Math.round(this.paygSummary.avgPaymentThisMonth);
            this.paygSummary.creditStatusBalanceLastMonth = Math.round(this.paygSummary.creditStatusBalanceLastMonth);
            this.paygSummary.creditStatusBalanceThisMonth = Math.round(this.paygSummary.creditStatusBalanceThisMonth);

            if (this.paygSummary.totalProperties && this.paygSummary.totalProperties > 0) this.paygConsumersPercentage = Math.round((this.paygSummary.paygproperties / this.paygSummary.totalProperties) * 100);
            if (this.paygSummary.paygproperties && this.paygSummary.paygproperties > 0) this.websiteUsersPercentage = Math.round((this.paygSummary.websiteUsers / this.paygSummary.paygproperties) * 100);

            this.isIncreasing = this.paygSummary.paygpropertiesChange && this.paygSummary.paygpropertiesChange > 0 ? true : false;
            this.isDecreasing = this.paygSummary.paygpropertiesChange && this.paygSummary.paygpropertiesChange < 0 ? true : false;

        });

        this.paygService.getCumulativeCreditStatus(this.clientId, siteId, null).then().then((result) => {
            this.cumulativeCreditStatus = [];
            if (result && result.length > 0) {
                this.cumulativeCreditStatus = result;
            }
        });
    }

    onInfoClick(widget: number) {
        if (widget === 1) {
            swal({
                title: 'Top-up choices',
                html: '<p style="text-align: left;">This chart is showing you all payments (top-ups) made by consumers in you PAYG portfolio.</p>\
              <p style="text-align: left;">Shown in value of all payments in each Month & Year as per the number of full months in the page filter and current month to date, by payment method type.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }

        if (widget === 2) {
            swal({
                title: 'Cumulative Credit Status',
                html: '<p style="text-align: left;">This chart is displaying the total, by latest each month, of all negative credit status results and all positive credit status results.</p>\
              <p style="text-align: left;">This indicates the overall behaviour of your PAYG portfolio customers.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }

        if (widget === 3) {
            swal({
                title: 'Payment Transmission Performance',
                html: '<p style="text-align: left;">This chart is considering the total payments made in the date filter set on the page, \
                                            and exposing the quantity of payments that transmit from payment made to the PAYG device within each minute quantity window.</p>\
              <p style="text-align: left;">In most cases payments should reach PAYG devices within 10 minutes. However, the time this can take can depend on the performance of the mobile network connection in your customers area.\
                                          If credit has not reached the PAYG device within 2 hours past the payment time, then it is indicative of a problem.</p>\
              <p style="text-align: left;">If continuously payments are taking longer to transmit than expected, we’d recommend speaking to Switch2,\
                                          there may be signal issues in your customers properties or other concerns we could investigate and resolve.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }
    }
}
